import { Icon } from "@iconify/react";
import { useCallback, useEffect, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";

// material
import searchFill from "@iconify/icons-eva/search-fill";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Card,
  CardHeader,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Document, Packer, Paragraph, TextRun } from "docx";
import moment from "moment";
import { buildDownloadFileName } from "../../utils/downloadFileName";
import ColoredLabel from "../../components/application-utils/ColoredLabel";
import {
  GridFlexEnd,
  RowCell,
  SearchStyle,
  TabCell,
} from "../../components/styled";
import baseService from "../../services/base.service";
import { Grid } from "@mui/material";
import { RecordStatus } from "../../enums/RecordStatus";
import {
  getAllRecordsByDepartmentIdAndStatus,
  removeSecretRecord,
} from "../../services/record.service";
import { handleSecretRecordAfterDownload } from "../../utils/secretRecordDownload";
import { useSnackbar } from "notistack";
import useAuth from "../../hooks/useAuth";
import RecordDeleteDialog from "../records/RecordDeleteDialog";

function formatEngineLabel(engine?: string | null) {
  if (!engine) return null;
  if (engine === "faster_whisper") return "Whisper";
  if (engine === "speechmatics") return "Boardnote";
  return engine;
}

const SEARCH_DEBOUNCE_MS = 300;

export default function FileTable() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState("1");
  const [records, setRecords] = useState<any>([]);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { companyInfo } = useAuth();

  function getTabValues() {
    if (tabValue === "1") return [];
    else if (tabValue === "2") return [RecordStatus.TRANSCRIPTED];
    else if (tabValue === "3")
      return [
        RecordStatus.TRANSCRIBING,
        RecordStatus.CANCELED,
        RecordStatus.FAILED,
      ];
    else return [];
  }

  const pageLimit = 5;

  const getAllRecordsByStatus = async (
    departmentId: number,
    status: Array<RecordStatus>,
    pageNumber: number,
    pageSize: number,
    searchQuery?: string,
  ) => {
    try {
      const response = await getAllRecordsByDepartmentIdAndStatus(
        departmentId,
        status,
        pageNumber,
        pageSize,
        searchQuery,
      );
      if (response?.status === 200) {
        setRecords(response?.data?.data);
      }
    } catch {
      enqueueSnackbar("Kayıtlar yüklenirken bir hata oluştu.", { variant: "error" });
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.job_id) return;
    setDeleteLoading(true);
    try {
      const response = await removeSecretRecord(deleteTarget.job_id);
      if (response?.status === 200) {
        enqueueSnackbar("Kayıt başarıyla silindi.", { variant: "success" });
        setDeleteTarget(null);
        if (companyInfo?.department_id) {
          await getAllRecordsByStatus(
            companyInfo.department_id,
            getTabValues(),
            page,
            pageLimit,
            debouncedSearch,
          );
        }
        return;
      }
      enqueueSnackbar("Kayıt silinemedi.", { variant: "error" });
    } catch {
      enqueueSnackbar("Kayıt silinemedi.", { variant: "error" });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: any) => {
      setTabValue(newValue);
    },
    [],
  );

  function FileRow({
    row,
    onRequestDelete,
  }: {
    row: any;
    onRequestDelete: (row: any) => void;
  }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: any) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleRoute = (url: any) => {
      window.location.pathname = url;
    };

    const engineLabel = formatEngineLabel(row?.engine);
    const currentUserEmail = companyInfo?.user_email?.toLowerCase();
    const sharedWithEmail = row?.shared_with_email?.toLowerCase();
    const sharedWithEmails: string[] = (row?.shared_with_emails ?? []).map((e: string) => e.toLowerCase());
    const ownerEmail = row?.user?.email?.toLowerCase();
    const isRecordOwner = !!currentUserEmail && ownerEmail === currentUserEmail;
    const isSharedWithCurrentUser =
      !!currentUserEmail &&
      ownerEmail !== currentUserEmail &&
      (sharedWithEmail === currentUserEmail ||
        sharedWithEmails.includes(currentUserEmail));

    const parseSpeakerGroups = (richResult: any): any[] => {
      try {
        if (!richResult) return [];
        if (Array.isArray(richResult)) return richResult;
        if (typeof richResult === "string") {
          const parsed = JSON.parse(richResult);
          if (Array.isArray(parsed)) return parsed;
          if (Array.isArray(parsed?.speaker_groups)) return parsed.speaker_groups;
          return [];
        }
        if (Array.isArray(richResult?.speaker_groups)) return richResult.speaker_groups;
        return [];
      } catch {
        return [];
      }
    };

    const buildSpeakerParagraphs = (groups: any[]): Paragraph[] => {
      const lines: Paragraph[] = [];
      groups.forEach((g: any) => {
        let line = "";
        g.words?.forEach((w: any) => {
          if (w.type === "word") line += (line ? " " : "") + w.text;
          else if (w.type === "punctuation") line += w.text;
          else if (w?.text) line += (line ? " " : "") + w.text;
        });
        lines.push(
          new Paragraph({
            children: [new TextRun(`[${g.speaker ?? "Konuşmacı"}]: ${line.trim()}`)],
          }),
        );
      });
      return lines;
    };

    const downloadTxtFile = async (
      id: any,
      is_secret: boolean,
      record_name?: string | null,
      created_date?: string | null,
    ) => {
      baseService
        .get("/webhooks/record_result?recordId=" + id)
        .then((res) => {
          const richResult = res?.data?.data?.richResult ?? res?.data?.data?.rich_result;
          const groups = parseSpeakerGroups(richResult);
          let line: Paragraph[] = [];
          if (groups.length > 0) {
            line = buildSpeakerParagraphs(groups);
          } else {
            enqueueSnackbar("Rich transcript parse edilemedi, düz transkript kullanıldı.", {
              variant: "warning",
            });
            const record_result = res?.data?.data?.hashedResult ?? "";
            const splitted_result = record_result.split("\n");
            line = splitted_result.map(
              (item: any) =>
                new Paragraph({
                  children: [new TextRun(item)],
                }),
            );
          }
          const doc = new Document({
            sections: [
              {
                properties: {},
                children: line,
              },
            ],
          });
          Packer.toBlob(doc)
            .then((blob) => {
              // saveAs from FileSaver will download the file
              const element = document.createElement("a");
              element.href = URL.createObjectURL(blob);
              element.download = buildDownloadFileName(record_name, created_date);
              // simulate link click
              document.body.appendChild(element);
              element.click();
            })
            .then(async () => {
              await handleSecretRecordAfterDownload(id, is_secret, {
                enqueueSnackbar,
                onRemoved: async () => {
                  if (companyInfo?.department_id) {
                    await getAllRecordsByStatus(
                      companyInfo?.department_id,
                      getTabValues(),
                      page,
                      pageLimit,
                      debouncedSearch,
                    );
                  }
                },
              });
            });
        })
        .catch((error) => {
          enqueueSnackbar(
            "Dosya indirilirken bir hata meydana geldi: " + error,
          );
          console.error(error);
        });
    };

    return (
      <TableRow key={row?.id}>
        <RowCell>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography variant="subtitle2">{row?.user?.full_name}</Typography>
            {isSharedWithCurrentUser ? (
              <Tooltip title="Sizinle paylaşıldı">
                <Box
                  component="span"
                  sx={{ display: "inline-flex", color: "info.main", flexShrink: 0 }}
                >
                  <Icon icon="mdi:share-variant-outline" width={18} />
                </Box>
              </Tooltip>
            ) : null}
          </Stack>
        </RowCell>
        <RowCell>{row?.user?.department_name}</RowCell>
        <RowCell>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="subtitle2" component="span">
              {row?.record_name}
            </Typography>
            {engineLabel ? (
              <Chip
                label={engineLabel}
                size="small"
                variant="outlined"
                color="primary"
              />
            ) : null}
          </Stack>
        </RowCell>
        {/* <RowCell>{row?.duration}</RowCell> */}
        <RowCell>
          {moment(row?.created_date).format("DD/MM/YYYY HH:mm")}
        </RowCell>
        <RowCell
          sx={{
            whiteSpace: "normal",
            overflow: "visible",
            maxWidth: "none",
            minWidth: 220,
          }}
        >
          <ColoredLabel status={row?.status} processingPhase={row?.processing_phase} />
        </RowCell>
        <TableCell>
          <IconButton
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            onClick={handleClick}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                handleRoute(`/dashboard/file/${row?.id}`);
              }}
              sx={{ textDecoration: "none" }}
            >
              Detay
            </MenuItem>
            {isRecordOwner && row?.status !== "DELETED" ? (
              <MenuItem
                onClick={() => {
                  handleClose();
                  onRequestDelete(row);
                }}
                sx={{ color: "error.main" }}
              >
                Kaydı Sil
              </MenuItem>
            ) : null}
          </Menu>

          {row?.status === "TRANSCRIPTED" ? (
            <IconButton
              onClick={() =>
                downloadTxtFile(
                  row?.job_id,
                  row?.is_secret,
                  row?.record_name,
                  row?.created_date,
                )
              }
            >
              <FileDownloadOutlinedIcon />
            </IconButton>
          ) : null}
        </TableCell>
      </TableRow>
    );
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Single effect handles tab, page, and debounced search changes.
  useEffect(() => {
    if (!companyInfo?.department_id) return;

    const currentTabValues = getTabValues();

    getAllRecordsByStatus(
      companyInfo.department_id,
      currentTabValues,
      page,
      pageLimit,
      debouncedSearch,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, page, debouncedSearch, companyInfo]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
  };

  return (
    <>
      <Grid item sx={{ minWidth: "100%" }}>
        <Box sx={{ typography: "body1" }}>
          <TabContext value={tabValue}>
            <Box
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                pb: 1.2,
                mt: 4,
                mb: 4,
                borderBottomColor: "transparent",
              }}
            >
              <TabList onChange={handleTabChange} aria-label="" centered>
                <Tab
                  label="Tümü"
                  icon={
                    <Icon icon="healthicons:ui-user-profile" fontSize={24} />
                  }
                  value="1"
                />
                <Tab
                  label="Oluşturulmuş Dosyalar"
                  icon={<Icon icon="ant-design:profile-filled" fontSize={24} />}
                  value="2"
                />
                <Tab
                  label="Oluşturulmamış Dosyalar"
                  icon={<Icon icon="eva:bell-fill" fontSize={24} />}
                  value="3"
                />
              </TabList>
            </Box>
            <TabPanel value={tabValue}>
              <Card>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 2, px: 2, p: 2 }}
                >
                  <CardHeader title="Yüklenen Dosyalar" sx={{ mb: 3 }} />

                  <SearchStyle
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ara..."
                    startAdornment={
                      <InputAdornment position="start">
                        <Box
                          component={Icon}
                          icon={searchFill}
                          sx={{ color: "text.disabled" }}
                        />
                      </InputAdornment>
                    }
                  />
                </Stack>

                {/* <Scrollbar> */}
                <TableContainer sx={{ minWidth: 720 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TabCell>Yükleyenin Adı</TabCell>
                        <TabCell>Departman</TabCell>
                        <TabCell>Dosya İsmi</TabCell>
                        {/* <TabCell>Süre</TabCell> */}
                        <TabCell>Tarih</TabCell>
                        <TabCell sx={{ minWidth: 220, maxWidth: "none" }}>Durum</TabCell>
                        <TabCell></TabCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records?.result?.map((row: any) => (
                        <FileRow
                          key={row.id}
                          row={row}
                          onRequestDelete={setDeleteTarget}
                        />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* </Scrollbar> */}

                <GridFlexEnd sx={{ m: 4 }}>
                  <Pagination
                    count={records?.pager?.totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </GridFlexEnd>
              </Card>
            </TabPanel>
          </TabContext>
        </Box>
      </Grid>

      <RecordDeleteDialog
        open={deleteTarget != null}
        loading={deleteLoading}
        recordName={deleteTarget?.record_name}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
